﻿import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import { api, type OwnerRow } from "../app/api";
import { DeleteCodeConfirmModal } from "../components/common/DeleteCodeConfirmModal";
import { ServerDataTable } from "../components/tables/ServerDataTable";
import { isActionMenuEventTarget, useActionMenuAutoClose, useFloatingActionMenu } from "../hooks/useActionMenuAutoClose";
import { getAnredeDefinitionsFromSettings, getTitelDefinitionsFromSettings } from "../shared/ownerNaming";

type StatusOption = "" | "aktiv" | "inaktiv" | "keine_zuordnung";
type OwnerSortField = "objektnummer" | "eigentuemer_id" | "vollname";
type ReachabilityFilter = "" | "digital" | "analog";

type DeleteConfirmState = {
  id: string;
  label: string;
  code: string;
  input: string[];
  error: string;
} | null;

const hasAnyName = (name1: string, name2: string, name3: string) => !!name1.trim() || !!name2.trim() || !!name3.trim();

const formatDateDe = (value?: string) => {
  if (!value) return "-";
  const m = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : value;
};

const buildOwnerValidityTooltip = (owner: OwnerRow, statusFilter: StatusOption) => {
  const detailLines = (owner.objectRefs ?? [])
    .flatMap((objectRef) =>
      (objectRef.assignmentDetails ?? [])
        .filter((detail) => {
          if (statusFilter === "aktiv") return detail.status === "aktiv";
          if (statusFilter === "inaktiv") return detail.status === "inaktiv";
          return true;
        })
        .map((detail) => {
          const from = formatDateDe(detail.gueltigAb);
          const to = formatDateDe(detail.gueltigBis);
          return `Objekt ${detail.objektnummer} | Einheit ${detail.einheitsnummer} | EGT ab: ${from} | EGT bis: ${to}`;
        })
    )
    .filter(Boolean);

  const uniqueLines = Array.from(new Set(detailLines));
  if (uniqueLines.length > 0) return uniqueLines.join("\n");
  return `EGT ab: ${formatDateDe(owner.gueltig_ab)} | EGT bis: ${formatDateDe(owner.gueltig_bis)}`;
};

const formatOwnerStatusLabel = (status: OwnerRow["status"]) =>
  status === "keine_zuordnung" ? "keine Zuordnung" : status;

const parseReachabilityFilter = (value: string | null): ReachabilityFilter =>
  value === "digital" || value === "analog" ? value : "";

const buildOwnerDisplayName = (owner: OwnerRow) => {
  const parts = [owner.anrede, owner.titel, owner.vorname, owner.name_1, owner.name_2, owner.name_3]
    .map((value) => (value ?? "").trim())
    .filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  return owner.vollname?.trim() || "-";
};

type OwnersPageProps = {
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
};

export const OwnersPage = ({ canCreate = false, canUpdate = false, canDelete = false }: OwnersPageProps) => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const initialSearch = String(searchParams.get("search") ?? "").trim();
  const initialReachability = parseReachabilityFilter(searchParams.get("reachability") ?? searchParams.get("erreichbarkeit"));
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(initialSearch);
  const [objectFilter, setObjectFilter] = useState("");
  const [status, setStatus] = useState<StatusOption>("aktiv");
  const [reachability, setReachability] = useState<ReachabilityFilter>(initialReachability);
  const [sortBy, setSortBy] = useState<OwnerSortField>("objektnummer");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [showCreate, setShowCreate] = useState(false);
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [isActionMenuHovered, setIsActionMenuHovered] = useState(false);
  const [createError, setCreateError] = useState("");
  const [editError, setEditError] = useState("");
  const [actionError, setActionError] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState<DeleteConfirmState>(null);

  const [form, setForm] = useState({
    objectId: "",
    eigentuemer_id: "",
    anrede: "", titel: "", vorname: "",
    name_1: "",
    name_2: "",
    name_3: "",
    strasse: "",
    plz: "",
    ort: "",
    land_code: "",
    land: "",
    email: "",
    telefon: "",
    status: "" as StatusOption
  });

  const [editForm, setEditForm] = useState<null | {
    id: string;
    objectId: string;
    eigentuemer_id: string;
    anrede: string; titel: string; vorname: string;
    name_1: string;
    name_2: string;
    name_3: string;
    strasse: string;
    plz: string;
    ort: string;
    land_code: string;
    land: string;
    email: string;
    telefon: string;
    status: "aktiv" | "inaktiv";
  }>(null);

  const { data, isError } = useQuery({
    queryKey: ["owners", page, pageSize, search, objectFilter, status, reachability, sortBy, sortDir],
    queryFn: () =>
      api.owners({
        page,
        pageSize,
        search,
        objectId: objectFilter || undefined,
        status: status || undefined,
        erreichbarkeit: reachability || undefined,
        sortBy,
        sortDir
      }),
    placeholderData: (previousData) => previousData,
    staleTime: 15_000
  });

  const { data: objectsData } = useQuery({
    queryKey: ["objects-select"],
    queryFn: () => api.objects({ page: 1, pageSize: 200, sortBy: "objektnummer", sortDir: "asc" })
  });

  const { data: activeResolutions } = useQuery({
    queryKey: ["active-resolutions-lock"],
    queryFn: () => api.resolutions({ page: 1, pageSize: 500, status: "aktiv" })
  });
  const { data: uiSettings } = useQuery({
    queryKey: ["ui-settings-owner-anrede"],
    queryFn: () => api.uiSettings(),
    staleTime: 60_000
  });

  const lockedObjectIds = useMemo(() => new Set((activeResolutions?.data ?? []).map((r) => r.objectId)), [activeResolutions?.data]);
  const anredeOptions = useMemo(
    () => getAnredeDefinitionsFromSettings(uiSettings?.management),
    [uiSettings?.management]
  );
  const titelOptions = useMemo(
    () => getTitelDefinitionsFromSettings(uiSettings?.management),
    [uiSettings?.management]
  );

  const createMutation = useMutation({
    mutationFn: () =>
      api.createOwner({
        eigentuemer_id: form.eigentuemer_id.trim(),
        anrede: form.anrede.trim() || undefined,
        titel: form.titel.trim() || undefined,
        vorname: form.vorname.trim() || undefined,
        name_1: form.name_1.trim() || undefined,
        name_2: form.name_2.trim() || undefined,
        name_3: form.name_3.trim() || undefined,
        strasse: form.strasse.trim() || undefined,
        plz: form.plz.trim() || undefined,
        ort: form.ort.trim() || undefined,
        land_code: form.land_code.trim() || undefined,
        land: form.land.trim() || undefined,
        email: form.email.trim(),
        telefon: form.telefon.trim() || undefined,
        status: form.status as "aktiv" | "inaktiv"
      }),
    onSuccess: async () => {
      setCreateError("");
      setShowCreate(false);
      setForm({ objectId: "", eigentuemer_id: "", anrede: "", titel: "", vorname: "", name_1: "", name_2: "", name_3: "", strasse: "", plz: "", ort: "", land_code: "", land: "", email: "", telefon: "", status: "" });
      await queryClient.invalidateQueries({ queryKey: ["owners"] });
      setPage(1);
    },
    onError: (error) => setCreateError(error instanceof Error ? error.message : "Anlage fehlgeschlagen")
  });

  const editMutation = useMutation({
    mutationFn: () => {
      if (!editForm) throw new Error("Kein Datensatz ausgewählt");
      if (lockedObjectIds.has(editForm.objectId)) throw new Error("Eigentümer ist wegen aktivem Umlaufbeschluss gesperrt");
      return api.updateOwner(editForm.id, {
        objectId: editForm.objectId,
        eigentuemer_id: editForm.eigentuemer_id.trim(),
        anrede: editForm.anrede.trim() || undefined,
        titel: editForm.titel.trim() || undefined,
        vorname: editForm.vorname.trim() || undefined,
        name_1: editForm.name_1.trim() || undefined,
        name_2: editForm.name_2.trim() || undefined,
        name_3: editForm.name_3.trim() || undefined,
        strasse: editForm.strasse.trim() || undefined,
        plz: editForm.plz.trim() || undefined,
        ort: editForm.ort.trim() || undefined,
        land_code: editForm.land_code.trim() || undefined,
        land: editForm.land.trim() || undefined,
        email: editForm.email.trim(),
        telefon: editForm.telefon.trim() || undefined,
        status: editForm.status
      });
    },
    onSuccess: async () => {
      setEditError("");
      setEditForm(null);
      await queryClient.invalidateQueries({ queryKey: ["owners"] });
    },
    onError: (error) => setEditError(error instanceof Error ? error.message : "Bearbeitung fehlgeschlagen")
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deactivateOwner(id),
    onSuccess: async () => {
      setDeleteConfirm(null);
      setActionError("");
      await queryClient.invalidateQueries({ queryKey: ["owners"] });
    },
    onError: (error) => setActionError(error instanceof Error ? error.message : "Löschen fehlgeschlagen")
  });

  useEffect(() => {
    setPage(1);
  }, [search, objectFilter, status, reachability, sortBy, sortDir, pageSize]);

  useEffect(() => {
    setOpenActionMenuId(null);
  }, [page, search, objectFilter, status, reachability, sortBy, sortDir, showCreate, editForm]);

  useEffect(() => {
    if (!openActionMenuId) return;
    const closeMenu = (event?: MouseEvent) => {
      if (event && isActionMenuEventTarget(event.target)) return;
      setOpenActionMenuId(null);
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") closeMenu();
    };
    window.addEventListener("mousedown", closeMenu);
    window.addEventListener("keydown", handleEscape);
    return () => {
      window.removeEventListener("mousedown", closeMenu);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [openActionMenuId]);

  useActionMenuAutoClose({
    isOpen: !!openActionMenuId,
    isHovered: isActionMenuHovered,
    closeMenu: () => setOpenActionMenuId(null),
    timeoutMs: 3000
  });

  useEffect(() => {
    if (!openActionMenuId) setIsActionMenuHovered(false);
  }, [openActionMenuId]);

  const { actionMenuPosition, openActionMenuAtTrigger } = useFloatingActionMenu({
    openMenuId: openActionMenuId,
    setOpenMenuId: setOpenActionMenuId
  });

  const toggleSort = (field: OwnerSortField) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(field);
    setSortDir("asc");
  };

  const sortLabel = (field: OwnerSortField, label: ReactNode) => (
    <button
      type="button"
      onClick={() => toggleSort(field)}
      style={{
        border: "none",
        background: "transparent",
        padding: 0,
        margin: 0,
        cursor: "pointer",
        font: "inherit",
        color: "inherit",
        fontWeight: 700,
        display: "inline-flex",
        alignItems: "center",
        gap: 6
      }}
      title={sortBy === field ? `Sortierung: ${sortDir === "asc" ? "aufsteigend" : "absteigend"}` : "Sortierung aktivieren"}
    >
      <span>{label}</span>
      <span aria-hidden="true">{sortBy === field ? (sortDir === "asc" ? "↑" : "↓") : "↕"}</span>
    </button>
  );

  const columns = useMemo(
    () => [
      {
        key: "objekt_refs",
        label: <>Eigentümer<br />in Objekt</>,
        align: "center" as const,
        width: 126,
        render: (r: OwnerRow) => {
          type ObjectRefEntry = NonNullable<OwnerRow["objectRefs"]>[number];
          const refs = (r.objectRefs ?? []).filter((entry) => !!entry.id && !!entry.objektnummer);
          const fallbackRef: ObjectRefEntry[] = r.objectId && r.objektnummer
            ? [{ id: r.objectId, objektnummer: r.objektnummer, status: r.status === "aktiv" ? "aktiv" : "inaktiv", assignmentDetails: [] }]
            : [];
          const displayRefs = refs.length > 0 ? refs : fallbackRef;
          return (
            <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
              {displayRefs.map((entry) => (
                <Link
                  key={`${r.id}-${entry.id}`}
                  to={`/objects/${entry.id}`}
                  className={`badge object-ref ${entry.status === "inaktiv" ? "object-ref-inaktiv" : ""}`}
                  style={{ textDecoration: "none" }}
                >
                  {entry.objektnummer}
                </Link>
              ))}
            </span>
          );
        }
      },
      {
        key: "eigentuemer_id",
        label: sortLabel("eigentuemer_id", "Eigentümer ID"),
        align: "center" as const
      },
      { key: "anrede", label: "Anrede" },
      { key: "titel", label: "Titel" },
      { key: "vorname", label: "Vorname" },
      { key: "name_1", label: sortLabel("vollname", "Name 1") },
      { key: "name_2", label: "Name 2" },
      { key: "name_3", label: "Name 3" },
      { key: "email", label: "E-Mail" },
      { key: "telefon", label: "Telefon" },
      {
        key: "erreichbarkeit",
        label: "Erreichbarkeit",
        align: "center" as const,
        render: (r: OwnerRow) => <span className={`badge ${r.ist_analog ? "erreichbar-analog" : "erreichbar-digital"}`}>{r.ist_analog ? "analog" : "digital"}</span>
      },
      {
        key: "status",
        label: "Status",
        render: (r: OwnerRow) => (
          <span className={`badge ${r.status} quick-tooltip quick-tooltip-subtle quick-tooltip-multiline`} data-tooltip={buildOwnerValidityTooltip(r, status)} tabIndex={0}>
            {formatOwnerStatusLabel(r.status)}
          </span>
        )
      },
      {
        key: "actions",
        label: "Aktionen",
        render: (r: OwnerRow) => {
          if (!canUpdate && !canDelete) return <span style={{ color: "var(--text-soft)" }}>-</span>;
          const locked = lockedObjectIds.has(r.objectId);
          return (
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              {canUpdate ? (
                <button
                  onClick={() =>
                    setEditForm({
                      id: r.id,
                      objectId: r.objectId ?? "",
                      eigentuemer_id: r.eigentuemer_id,
                      anrede: r.anrede ?? "", titel: r.titel ?? "", vorname: r.vorname ?? "",
                      name_1: r.name_1 ?? "",
                      name_2: r.name_2 ?? "",
                      name_3: r.name_3 ?? "",
                      strasse: r.strasse ?? "",
                      plz: r.plz ?? "",
                      ort: r.ort ?? "",
                      land_code: r.land_code ?? "",
                      land: r.land ?? "",
                      email: r.email ?? "",
                      telefon: r.telefon ?? "",
                      status: r.status === "inaktiv" ? "inaktiv" : "aktiv"
                    })
                  }
                  disabled={locked}
                  title={locked ? "Gesperrt wegen aktivem Umlaufbeschluss" : undefined}
                >
                  Bearbeiten
                </button>
              ) : null}
              {canDelete ? (
                <button
                  onClick={() => {
                    if (locked) {
                      setActionError("Eigentümer ist wegen aktivem Umlaufbeschluss gesperrt.");
                      return;
                    }
                    setDeleteConfirm({
                      id: r.id,
                      label: `${r.objektnummer} / ${r.eigentuemer_id}`,
                      code: String(Math.floor(1000 + Math.random() * 9000)),
                      input: ["", "", "", ""],
                      error: ""
                    });
                  }}
                  disabled={locked || deleteMutation.isPending}
                  title={locked ? "Löschen bei aktivem Umlaufbeschluss gesperrt" : undefined}
                >
                  Löschen
                </button>
              ) : null}
              {locked && (canUpdate || canDelete) ? (
                <span className="lock-indicator" title="Gesperrt wegen aktivem Umlaufbeschluss">
                  {"\uD83D\uDD12"}
                </span>
              ) : null}
            </div>
          );
        }
      }
    ],
    [canDelete, canUpdate, deleteMutation.isPending, lockedObjectIds, sortBy, sortDir]
  );

  const canCreateForm = form.eigentuemer_id.trim() && hasAnyName(form.name_1, form.name_2, form.name_3) && form.status;
  const canEdit = editForm && editForm.eigentuemer_id.trim() && hasAnyName(editForm.name_1, editForm.name_2, editForm.name_3);
  const rows = data?.data ?? [];
  const totalRows = data?.meta.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const isFrameworkV2 =
    uiSettings?.management?.uiFramework === "mockup_v2" ||
    (typeof document !== "undefined" && document.body.classList.contains("framework-v2"));
  const fixedViewportListLayout = isFrameworkV2 && !showCreate && !editForm;
  const pageItems = useMemo(() => {
    if (totalPages <= 1) return [1];
    const start = Math.max(1, page - 1);
    const end = Math.min(totalPages, start + 2);
    const adjustedStart = Math.max(1, end - 2);
    return Array.from({ length: end - adjustedStart + 1 }, (_, i) => adjustedStart + i);
  }, [page, totalPages]);

  const ownerTitleIcon = (
    <svg viewBox="0 0 24 24" fill="none">
      <circle cx="9.1" cy="9.1" r="2.5" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="15.8" cy="8.4" r="2.1" stroke="currentColor" strokeWidth="1.8" />
      <path d="M4.7 18.6a5 5 0 0 1 8.8 0M12.2 18.6a4.2 4.2 0 0 1 7.1 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );

  const icons = {
    search: (
      <svg viewBox="0 0 20 20" fill="none">
        <circle cx="9" cy="9" r="5.8" stroke="currentColor" strokeWidth="1.8" />
        <path d="m13.2 13.2 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
    reset: (
      <svg viewBox="0 0 20 20" fill="none">
        <path d="M5.2 6.3V3.9H2.8M4 4a6.4 6.4 0 1 1-1.4 7.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    object: (
      <svg viewBox="0 0 20 20" fill="none">
        <path d="M6.2 5.2h7.6v9.6H6.2z" stroke="currentColor" strokeWidth="1.7" />
        <path d="M6.2 8.4h7.6M6.2 11.6h7.6M9.4 5.2v9.6M12.6 5.2v9.6" stroke="currentColor" strokeWidth="1.4" />
      </svg>
    ),
    id: (
      <svg viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="7.4" r="2.5" stroke="currentColor" strokeWidth="1.7" />
        <path d="M5.2 15.9a4.8 4.8 0 0 1 9.6 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    ),
    user: (
      <svg viewBox="0 0 24 24" fill="none">
        <circle cx="9.1" cy="9.1" r="2.5" stroke="currentColor" strokeWidth="1.8" />
        <circle cx="15.8" cy="8.4" r="2.1" stroke="currentColor" strokeWidth="1.8" />
        <path d="M4.7 18.6a5 5 0 0 1 8.8 0M12.2 18.6a4.2 4.2 0 0 1 7.1 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    ),
    mail: (
      <svg viewBox="0 0 20 20" fill="none">
        <rect x="3.8" y="5.2" width="12.4" height="9.6" rx="1.6" stroke="currentColor" strokeWidth="1.7" />
        <path d="m4.5 6 5.5 4.1L15.5 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    phone: (
      <svg viewBox="0 0 20 20" fill="none">
        <path d="M6.4 3.8 4.8 5.4c-.8.8-.7 2.6.3 4.5 1 1.8 2.8 3.7 4.7 4.8 1.9 1.1 3.7 1.2 4.5.4l1.6-1.6-3-2.4-1.3 1.3c-.3.3-1 .1-1.9-.5-.8-.6-1.7-1.5-2.2-2.3-.6-.9-.8-1.6-.5-1.9l1.3-1.3-1.9-2.6Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    nonDigital: (
      <svg viewBox="0 0 20 20" fill="none">
        <rect x="5.4" y="2.8" width="9.2" height="14.4" rx="1.9" stroke="currentColor" strokeWidth="1.7" />
        <path d="M8.1 5.8h3.8M7.4 14.1l5.2-5.2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
      </svg>
    ),
    status: (
      <svg viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="10" r="5.9" stroke="currentColor" strokeWidth="1.7" />
        <path d="m10 7.2 1.8 2.5L10 12.8 8.2 9.7 10 7.2Z" fill="currentColor" />
      </svg>
    ),
    actions: (
      <svg viewBox="0 0 20 20" fill="none">
        <circle cx="10" cy="5.3" r="1.4" fill="currentColor" />
        <circle cx="10" cy="10" r="1.4" fill="currentColor" />
        <circle cx="10" cy="14.7" r="1.4" fill="currentColor" />
      </svg>
    )
  };

  const plainHeader = (label: ReactNode, icon: ReactNode) => (
    <span className="resolutions-v2-th-plain">
      <span className="resolutions-v2-th-icon" aria-hidden="true">{icon}</span>
      <span className="resolutions-v2-th-label">{label}</span>
    </span>
  );

  const renderSortIndicator = (field: OwnerSortField) => (
    <span className="resolutions-v2-sort-indicator" aria-hidden="true">
      {sortBy === field ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  const renderV2SortHeader = (field: OwnerSortField, label: ReactNode, icon: ReactNode) => (
    <button type="button" className="resolutions-v2-th-sort" onClick={() => toggleSort(field)}>
      <span className="resolutions-v2-th-icon" aria-hidden="true">{icon}</span>
      <span className="resolutions-v2-th-label">{label}</span>
      {renderSortIndicator(field)}
    </button>
  );

  const resetListFilters = () => {
    setSearch("");
    setObjectFilter("");
    setReachability("");
    setStatus("aktiv");
    setSortBy("objektnummer");
    setSortDir("asc");
    setPage(1);
  };

  return (
    <div className={fixedViewportListLayout ? "resolutions-v2-page" : undefined}>
      <section className={`page-header panel ${isFrameworkV2 ? "resolutions-v2-page-header" : ""}`}>
        <h2 className={isFrameworkV2 ? "resolutions-v2-title" : ""} style={{ margin: 0 }}>
          {isFrameworkV2 ? <span className="resolutions-v2-title-icon" aria-hidden="true">{ownerTitleIcon}</span> : null}
          {isFrameworkV2 ? (
            <span className="resolutions-v2-title-copy">
              <span className="resolutions-v2-title-main">Eigentümer</span>
              <span className="resolutions-v2-title-sub">Übersicht aller Eigentümer und Stammdaten</span>
            </span>
          ) : (
            <span>Eigentümer</span>
          )}
        </h2>
        {canCreate ? (
          <button className={isFrameworkV2 ? "resolutions-v2-create-btn" : ""} onClick={() => setShowCreate((v) => !v)}>
            {isFrameworkV2 && !showCreate ? (
              <span className="resolutions-v2-create-btn-icon" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none">
                  <path d="M10 4.2v11.6M4.2 10h11.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                </svg>
              </span>
            ) : null}
            <span>{showCreate ? "Schließen" : "Neuer Eigentümer"}</span>
          </button>
        ) : null}
      </section>

      {canCreate && showCreate ? (
        <section className="panel create-form">
          <h3 style={{ marginTop: 0 }}>Eigentümer anlegen</h3>
          <div className="form-grid">
            <div className="form-field"><label>Eigentümer ID*</label><input value={form.eigentuemer_id} onChange={(e) => setForm({ ...form, eigentuemer_id: e.target.value })} /></div>
            <div className="form-field"><label>Anrede</label><select value={form.anrede} onChange={(e) => setForm({ ...form, anrede: e.target.value })}><option value="">Anrede wählen</option>{anredeOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div className="form-field"><label>Titel</label><select value={form.titel} onChange={(e) => setForm({ ...form, titel: e.target.value })}><option value="">Titel wählen</option>{titelOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div className="form-field"><label>Vorname</label><input value={form.vorname} onChange={(e) => setForm({ ...form, vorname: e.target.value })} /></div><div className="form-field"><label>Name 1*</label><input value={form.name_1} onChange={(e) => setForm({ ...form, name_1: e.target.value })} /></div>
            <div className="form-field"><label>Name 2</label><input value={form.name_2} onChange={(e) => setForm({ ...form, name_2: e.target.value })} /></div>
            <div className="form-field"><label>Name 3</label><input value={form.name_3} onChange={(e) => setForm({ ...form, name_3: e.target.value })} /></div>
            <div className="form-field"><label>Straße / Postfach</label><input value={form.strasse} onChange={(e) => setForm({ ...form, strasse: e.target.value })} /></div>
            <div className="form-field"><label>PLZ</label><input value={form.plz} onChange={(e) => setForm({ ...form, plz: e.target.value })} /></div>
            <div className="form-field"><label>Ort</label><input value={form.ort} onChange={(e) => setForm({ ...form, ort: e.target.value })} /></div>
            <div className="form-field"><label>Länder-Code</label><input value={form.land_code} onChange={(e) => setForm({ ...form, land_code: e.target.value.toUpperCase() })} /></div>
            <div className="form-field"><label>Land</label><input value={form.land} onChange={(e) => setForm({ ...form, land: e.target.value })} /></div>
            <div className="form-field"><label>E-Mail</label><input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="form-field"><label>Telefon</label><input value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} /></div>
            <div className="form-field"><label>Status*</label><select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value as StatusOption })}><option value="">Status wählen*</option><option value="aktiv">Aktiv</option><option value="inaktiv">Inaktiv</option></select></div>
          </div>
          <div className="form-actions">
            <button disabled={!canCreateForm || createMutation.isPending} onClick={() => createMutation.mutate()}>
              {createMutation.isPending ? "Speichere..." : "Eigentümer speichern"}
            </button>
            {createError ? <span className="error-text">{createError}</span> : null}
          </div>
        </section>
      ) : null}

      {!showCreate && canUpdate && editForm ? (
        <section className="panel create-form">
          <h3 style={{ marginTop: 0 }}>Eigentümer bearbeiten</h3>
          <div className="form-grid">
            <div className="form-field"><label>Eigentümer ID*</label><input value={editForm.eigentuemer_id} onChange={(e) => setEditForm({ ...editForm, eigentuemer_id: e.target.value })} /></div>
            <div className="form-field"><label>Anrede</label><select value={editForm.anrede} onChange={(e) => setEditForm({ ...editForm, anrede: e.target.value })}><option value="">Anrede wählen</option>{anredeOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div className="form-field"><label>Titel</label><select value={editForm.titel} onChange={(e) => setEditForm({ ...editForm, titel: e.target.value })}><option value="">Titel wählen</option>{titelOptions.map((opt) => <option key={opt} value={opt}>{opt}</option>)}</select></div>
            <div className="form-field"><label>Vorname</label><input value={editForm.vorname} onChange={(e) => setEditForm({ ...editForm, vorname: e.target.value })} /></div><div className="form-field"><label>Name 1*</label><input value={editForm.name_1} onChange={(e) => setEditForm({ ...editForm, name_1: e.target.value })} /></div>
            <div className="form-field"><label>Name 2</label><input value={editForm.name_2} onChange={(e) => setEditForm({ ...editForm, name_2: e.target.value })} /></div>
            <div className="form-field"><label>Name 3</label><input value={editForm.name_3} onChange={(e) => setEditForm({ ...editForm, name_3: e.target.value })} /></div>
            <div className="form-field"><label>Straße / Postfach</label><input value={editForm.strasse} onChange={(e) => setEditForm({ ...editForm, strasse: e.target.value })} /></div>
            <div className="form-field"><label>PLZ</label><input value={editForm.plz} onChange={(e) => setEditForm({ ...editForm, plz: e.target.value })} /></div>
            <div className="form-field"><label>Ort</label><input value={editForm.ort} onChange={(e) => setEditForm({ ...editForm, ort: e.target.value })} /></div>
            <div className="form-field"><label>Länder-Code</label><input value={editForm.land_code} onChange={(e) => setEditForm({ ...editForm, land_code: e.target.value.toUpperCase() })} /></div>
            <div className="form-field"><label>Land</label><input value={editForm.land} onChange={(e) => setEditForm({ ...editForm, land: e.target.value })} /></div>
            <div className="form-field"><label>E-Mail</label><input value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
            <div className="form-field"><label>Telefon</label><input value={editForm.telefon} onChange={(e) => setEditForm({ ...editForm, telefon: e.target.value })} /></div>
            <div className="form-field"><label>Status</label><select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value as "aktiv" | "inaktiv" })}><option value="aktiv">Aktiv</option><option value="inaktiv">Inaktiv</option></select></div>
          </div>
          <div className="form-actions">
            <button disabled={!canEdit || editMutation.isPending} onClick={() => editMutation.mutate()}>
              {editMutation.isPending ? "Speichere..." : "Änderungen speichern"}
            </button>
            <button onClick={() => setEditForm(null)}>Abbrechen</button>
            {editError ? <span className="error-text">{editError}</span> : null}
          </div>
        </section>
      ) : null}

      {!showCreate && isError ? <section className="panel">Fehler beim Laden der Eigentümer.</section> : null}
      {!showCreate && actionError ? <section className="panel error-text">{actionError}</section> : null}
      {!showCreate ? (
        isFrameworkV2 ? (
          <section className="panel resolutions-v2-list-panel">
            <div className="resolutions-v2-filterbar">
              <div className="resolutions-v2-filter-fields">
                <label className="resolutions-v2-filter-input">
                  <span className="resolutions-v2-filter-icon" aria-hidden="true">{icons.search}</span>
                  <input placeholder="Suche..." value={search} onChange={(e) => setSearch(e.target.value)} />
                </label>
                <select value={objectFilter} onChange={(e) => setObjectFilter(e.target.value)}>
                  <option value="">Alle Objekt-Nr</option>
                  {(objectsData?.data ?? []).map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.objektnummer}
                    </option>
                  ))}
                </select>
                <select value={reachability} onChange={(e) => setReachability(e.target.value as ReachabilityFilter)}>
                  <option value="">Alle Erreichbarkeiten</option>
                  <option value="digital">Digital</option>
                  <option value="analog">Analog</option>
                </select>
                <select value={status} onChange={(e) => setStatus(e.target.value as StatusOption)}>
                  <option value="">Alle Status</option>
                  <option value="aktiv">Aktiv</option>
                  <option value="inaktiv">Inaktiv</option>
                  <option value="keine_zuordnung">Keine Zuordnung</option>
                </select>
              </div>
              <button type="button" className="resolutions-v2-reset-btn" onClick={resetListFilters}>
                <span className="resolutions-v2-reset-btn-icon" aria-hidden="true">{icons.reset}</span>
                <span>Filter zurücksetzen</span>
              </button>
            </div>

            <div className="resolutions-v2-row-count">Zeilen: {rows.length} / Gesamt: {totalRows}</div>

            <div className="resolutions-v2-table-wrap">
              <table className="resolutions-v2-table owners-v2-table">
                <colgroup>
                  <col style={{ width: "126px" }} />
                  <col style={{ width: "184px" }} />
                  <col style={{ width: "460px" }} />
                  <col style={{ width: "200px" }} />
                  <col style={{ width: "156px" }} />
                  <col style={{ width: "168px" }} />
                  <col style={{ width: "112px" }} />
                  <col style={{ width: "92px" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>{plainHeader("Objekt-Nr", icons.object)}</th>
                    <th>{renderV2SortHeader("eigentuemer_id", "Eigentümer-ID", icons.id)}</th>
                    <th>{renderV2SortHeader("vollname", "Eigentümername", icons.user)}</th>
                    <th>{plainHeader("E-Mail", icons.mail)}</th>
                    <th>{plainHeader("Telefon", icons.phone)}</th>
                    <th>{plainHeader("Erreichbarkeit", icons.nonDigital)}</th>
                    <th>{plainHeader("Status", icons.status)}</th>
                    <th className="resolutions-v2-actions-head" aria-label="Aktionen" title="Aktionen">{plainHeader("", icons.actions)}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    type ObjectRefEntry = NonNullable<OwnerRow["objectRefs"]>[number];
                    const refs = (r.objectRefs ?? []).filter((entry) => !!entry.id && !!entry.objektnummer);
                    const fallbackRef: ObjectRefEntry[] = r.objectId && r.objektnummer
                      ? [{ id: r.objectId, objektnummer: r.objektnummer, status: r.status === "aktiv" ? "aktiv" : "inaktiv", assignmentDetails: [] }]
                      : [];
                    const displayRefs = refs.length > 0 ? refs : fallbackRef;
                    const locked = lockedObjectIds.has(r.objectId);
                    const menuId = r.id;
                    return (
                      <tr key={r.id}>
                        <td>
                          <span style={{ display: "inline-flex", flexWrap: "wrap", gap: 6, justifyContent: "center" }}>
                            {displayRefs.map((entry) => (
                              <Link
                                key={`${r.id}-${entry.id}`}
                                to={`/objects/${entry.id}`}
                                className={`badge object-ref ${entry.status === "inaktiv" ? "object-ref-inaktiv" : ""}`}
                                style={{ textDecoration: "none" }}
                              >
                                {entry.objektnummer}
                              </Link>
                            ))}
                          </span>
                        </td>
                        <td>{r.eigentuemer_id}</td>
                        <td className="resolutions-v2-object-cell">{buildOwnerDisplayName(r)}</td>
                        <td className="resolutions-v2-object-cell">{r.email || "-"}</td>
                        <td className="resolutions-v2-object-cell">{r.telefon || "-"}</td>
                        <td><span className={`badge ${r.ist_analog ? "erreichbar-analog" : "erreichbar-digital"}`}>{r.ist_analog ? "analog" : "digital"}</span></td>
                        <td>
                          <span className={`badge ${r.status} quick-tooltip quick-tooltip-subtle quick-tooltip-multiline`} data-tooltip={buildOwnerValidityTooltip(r, status)} tabIndex={0}>
                            {formatOwnerStatusLabel(r.status)}
                          </span>
                        </td>
                        <td>
                          {!canUpdate && !canDelete ? (
                            <span style={{ color: "var(--text-soft)" }}>-</span>
                          ) : (
                            <div className="resolutions-v2-action-wrap" onClick={(e) => e.stopPropagation()}>
                              <button
                                type="button"
                                className="resolutions-v2-action-trigger"
                                aria-label={`Aktionen für Eigentümer ${r.eigentuemer_id}`}
                                aria-expanded={openActionMenuId === menuId}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (openActionMenuId === menuId) {
                                    setOpenActionMenuId(null);
                                    return;
                                  }
                                  openActionMenuAtTrigger(menuId, e.currentTarget, canUpdate && canDelete ? 96 : 52);
                                }}
                              >
                                ⋮
                              </button>
                              {openActionMenuId === menuId && actionMenuPosition && typeof document !== "undefined" ? createPortal(
                                <div
                                  className="resolutions-v2-action-menu resolutions-v2-action-menu--fixed"
                                  style={{ left: actionMenuPosition.left, top: actionMenuPosition.top }}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  onMouseEnter={() => setIsActionMenuHovered(true)}
                                  onMouseLeave={() => setIsActionMenuHovered(false)}
                                >
                                  {canUpdate ? (
                                    <button
                                      type="button"
                                      disabled={locked}
                                      onClick={() => {
                                        setEditForm({
                                          id: r.id,
                                          objectId: r.objectId ?? "",
                                          eigentuemer_id: r.eigentuemer_id,
                                          anrede: r.anrede ?? "",
                                          titel: r.titel ?? "",
                                          vorname: r.vorname ?? "",
                                          name_1: r.name_1 ?? "",
                                          name_2: r.name_2 ?? "",
                                          name_3: r.name_3 ?? "",
                                          strasse: r.strasse ?? "",
                                          plz: r.plz ?? "",
                                          ort: r.ort ?? "",
                                          land_code: r.land_code ?? "",
                                          land: r.land ?? "",
                                          email: r.email ?? "",
                                          telefon: r.telefon ?? "",
                                          status: r.status === "inaktiv" ? "inaktiv" : "aktiv"
                                        });
                                        setOpenActionMenuId(null);
                                      }}
                                    >
                                      Bearbeiten
                                    </button>
                                  ) : null}
                                  {canDelete ? (
                                    <button
                                      type="button"
                                      disabled={locked || deleteMutation.isPending}
                                      onClick={() => {
                                        if (locked) {
                                          setActionError("Eigentümer ist wegen aktivem Umlaufbeschluss gesperrt.");
                                          setOpenActionMenuId(null);
                                          return;
                                        }
                                        setDeleteConfirm({
                                          id: r.id,
                                          label: `${r.objektnummer} / ${r.eigentuemer_id}`,
                                          code: String(Math.floor(1000 + Math.random() * 9000)),
                                          input: ["", "", "", ""],
                                          error: ""
                                        });
                                        setOpenActionMenuId(null);
                                      }}
                                    >
                                      Löschen
                                    </button>
                                  ) : null}
                                </div>,
                                document.body
                              ) : null}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="resolutions-v2-pagination">
              <div className="resolutions-v2-page-size">
                <select value={String(pageSize)} onChange={(e) => setPageSize(Number(e.target.value) || 10)}>
                  <option value="10">10 pro Seite</option>
                  <option value="20">20 pro Seite</option>
                  <option value="50">50 pro Seite</option>
                </select>
              </div>
              <div className="resolutions-v2-page-center">
                <button type="button" className="resolutions-v2-page-arrow" onClick={() => setPage((prev) => Math.max(1, prev - 1))} disabled={page <= 1}>‹</button>
                {pageItems.map((pageItem) => (
                  <button key={pageItem} type="button" className={`resolutions-v2-page-chip ${pageItem === page ? "active" : ""}`} onClick={() => setPage(pageItem)}>{pageItem}</button>
                ))}
                <button type="button" className="resolutions-v2-page-arrow" onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={page >= totalPages}>›</button>
                <span className="resolutions-v2-page-info">Seite {page} von {totalPages}</span>
              </div>
              <div className="resolutions-v2-page-next-wrap">
                <button type="button" className="resolutions-v2-next-btn" onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))} disabled={page >= totalPages}>
                  <span>Weiter</span>
                  <span aria-hidden="true">›</span>
                </button>
              </div>
            </div>
          </section>
        ) : (
          <ServerDataTable
            columns={columns}
            rows={rows}
            total={totalRows}
            page={page}
            pageSize={pageSize}
            onPageChange={setPage}
            bodyMaxHeight={560}
            topContent={
              <section className="filterbar" style={{ margin: 0 }}>
                <input placeholder="Suche" value={search} onChange={(e) => setSearch(e.target.value)} />
                <select value={objectFilter} onChange={(e) => setObjectFilter(e.target.value)}>
                  <option value="">Alle Objekt-Nr</option>
                  {(objectsData?.data ?? []).map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.objektnummer}
                    </option>
                  ))}
                </select>
                <select value={reachability} onChange={(e) => setReachability(e.target.value as ReachabilityFilter)}>
                  <option value="">Alle Erreichbarkeiten</option>
                  <option value="digital">Digital</option>
                  <option value="analog">Analog</option>
                </select>
                <select value={status} onChange={(e) => setStatus(e.target.value as StatusOption)}>
                  <option value="">Alle Status</option>
                  <option value="aktiv">Aktiv</option>
                  <option value="inaktiv">Inaktiv</option>
                  <option value="keine_zuordnung">Keine Zuordnung</option>
                </select>
              </section>
            }
          />
        )
      ) : null}

      <DeleteCodeConfirmModal
        open={!!deleteConfirm}
        message={deleteConfirm ? `Eigentümer ${deleteConfirm.label} soll gelöscht werden.` : ""}
        code={deleteConfirm?.code ?? ""}
        input={deleteConfirm?.input ?? ["", "", "", ""]}
        error={deleteConfirm?.error}
        isPending={deleteMutation.isPending}
        onClose={() => setDeleteConfirm(null)}
        onInputChange={(next) => {
          if (!deleteConfirm) return;
          setDeleteConfirm({ ...deleteConfirm, input: next });
        }}
        onErrorReset={() => {
          if (!deleteConfirm || !deleteConfirm.error) return;
          setDeleteConfirm({ ...deleteConfirm, error: "" });
        }}
        onConfirm={() => {
          if (!deleteConfirm) return;
          const enteredCode = deleteConfirm.input.join("");
          if (enteredCode !== deleteConfirm.code) {
            setDeleteConfirm({ ...deleteConfirm, error: "Sicherheitscode falsch eingegeben." });
            return;
          }
          deleteMutation.mutate(deleteConfirm.id);
        }}
      />
    </div>
  );
};




